import { SHOP_STATUS_STORE_ID } from "@/abilities/shop/config/constants";
import { FdiShop } from "@/abilities/shop/types/fdi-shop";
import { GenericShop } from "@/abilities/shop/types/generic-shop";
import { SalesForce } from "@/abilities/shop/types/salesforce-shop";
import { Sap } from "@/abilities/shop/types/sap-shop";
import { ShopCore } from "@/abilities/shop/types/shop-core";
import { ShopLight } from "@/abilities/shop/types/shop-light";
import { AccessAction, AccessFeature, AccessResource } from "@/shared/access-control/access-control";
import { useHasAccessAsync } from "@/shared/access-control/composables/use-has-access-async";
import { RemovableRef, StorageSerializers, get, set, useSessionStorage } from "@vueuse/core";
import gql from "graphql-tag";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { loadFdiShopConfig } from "../../fdi/api/fdi-shop-config-settings";
import { useFdiClient } from "../../fdi/composable/use-fdi-client";
import { loadIsGenericShopDemo } from "../../generic/api/load-is-generic-shop-demo";
import { canStartLogin, isSessionReady, loadGenericShopSession } from "../../generic/api/load-generic-shop-session";
import { loadSalesforceShopConfig } from "../../salesforce/api/salesforce-shop-config";
import { loadShopLightConfig } from "../../shop-light/api/shop-light-config";
import { loadSapShopConfig } from "../../sap/api/sap-shop-config";

/**
 * Store to manage the shop status and type.
 * It uses session storage to persist the state across sessions.
 * The store provides methods to check the connection, mock online/offline status,
 * and set the shop type and app entry path.
 */
export const useShopStatusStore = defineStore(SHOP_STATUS_STORE_ID, () => {
    // Define a session storage for the shop type
    const shopType: RemovableRef<ShopCore.Type | null> = useSessionStorage(SHOP_STATUS_STORE_ID + "shopType", null);

    // Define a session storage for the shop online status
    const isShopOnline = useSessionStorage(SHOP_STATUS_STORE_ID + "isOnline", false);

    // Define a session storage for the app entry path
    const appEntryPath = useSessionStorage(SHOP_STATUS_STORE_ID + "appEntryPath", "");

    /**
     * "Login attempted" marker. Set right before the user is sent to the shop login,
     * so the landing page knows the user is returning from the shop login
     * (see shop-landing-page.vue).
     * Kept in session storage, so it is still there when the shop login sends
     * the user back to the portal.
     * Saved at once (`flush: "sync"`), because the browser leaves the portal
     * immediately after it is set.
     */
    const genericLoginAttempted = useSessionStorage(SHOP_STATUS_STORE_ID + "genericLoginAttempted", false, {
        flush: "sync",
    });

    const config: RemovableRef<FdiShop.Config | SalesForce.Config | ShopLight.Config | Sap.Config | null> =
        useSessionStorage(SHOP_STATUS_STORE_ID + "config", null);

    // --- generic shop (QIT-6603), only populated behind the dev_generic_shop flag ---

    /** Whether the tenant has the generic shop feature flag. */
    const isGenericShopEnabled = useSessionStorage(SHOP_STATUS_STORE_ID + "isGenericShopEnabled", false);

    /**
     * Online + auth state of the generic shop.
     * An explicit serializer is required here: with a `null` default VueUse
     * falls back to its `any` serializer, which stringifies objects.
     */
    const genericShopSession: RemovableRef<GenericShop.Session | null> = useSessionStorage(
        SHOP_STATUS_STORE_ID + "genericShopSession",
        null,
        { serializer: StorageSerializers.object }
    );

    /**
     * True while the generic shop shows example data. Starts as true, so the
     * shop never looks real before the configuration has been read. Not saved:
     * it is read again at every start and every tab switch.
     */
    const isGenericShopDemo = ref(true);

    /**
     * True once this page has sent the browser to the generic shop login after
     * a lost session. Kept in memory, not in session storage: after the login
     * the page loads again, and a session lost later must start the login
     * again.
     */
    const genericShopLoginStarted = ref(false);

    /**
     * Mock functions to test shop offline behaviour in frontend.
     * Use it instead of the checkConnection function in config/fdi-shop.ability.ts
     */
    function mockStoreOffline() {
        set(isShopOnline, false);
        return false;
    }
    function mockStoreOnline() {
        set(isShopOnline, true);
        return true;
    }

    /**
     * QA-only: forces the generic shop session to an arbitrary
     * `ShopSessionStatus`, so `/shop-debug` (see `shop.ability.ts` — reachable
     * wherever `dev_generic_shop` is on, not just a local dev build) can demo
     * how the app reacts to every status without a real middleware. Same
     * spirit as `mockStoreOffline`/`mockStoreOnline` above, generalized to the
     * full generic-shop enum.
     */
    function mockGenericShopSession(status: GenericShop.Session["status"]) {
        set(shopType, ShopCore.Type.GENERIC);
        set(genericShopSession, { online: true, status, loginPath: "https://mock-generic-shop.example/login" });
        set(isShopOnline, isSessionReady({ online: true, status }));
    }

    /**
     * function to check, if shop can be reached via currency query
     * @returns true, if shop could be reached without errors, otherwise false
     */
    const checkFdiConnection = async () => {
        // try to get client without token & query currencies endpoint
        try {
            // try getting apollo client without any token
            const client = await useFdiClient().getHealthCheckClient();
            if (client) {
                const { data } = await client.query({
                    query: gql`
                        query healthCheck {
                            currencies {
                                name
                            }
                        }
                    `,
                });
                // for debug purpose
                const { currencies } = data;
                // when query returns data, we assume, that the shop is online
                if (currencies?.length >= 0) {
                    set(isShopOnline, true);
                    return true;
                }
            }
            set(isShopOnline, false);
            return false;
        } catch (e: unknown) {
            console.warn(`Currencies Query on provided shop url is not working properly.`);
            console.error(e);
            set(isShopOnline, false);
            return false;
        }
    };

    /**
     * function to check, if shop can be reached via currency query
     * @returns true, if shop could be reached without errors, otherwise false
     */
    const checkConnection = async (): Promise<boolean> => {
        if (shopType.value === ShopCore.Type.FDI) {
            return checkFdiConnection();
        }
        if (shopType.value === ShopCore.Type.GENERIC) {
            /*
             * The session read during initShopType already answered this, and
             * shop.ability.ts calls checkConnection() immediately afterwards — the
             * branch below would overwrite that verdict with an unconditional
             * "online". Re-reading the session instead would cost a second round
             * trip per ability init for no new information (QIT-7074).
             */
            return get(isShopOnline);
        }
        set(isShopOnline, true);
        return get(isShopOnline);
    };

    const fdiShopConfig = (): FdiShop.Config | null => {
        const tmpConf = get(config);
        if (shopType.value === ShopCore.Type.FDI && tmpConf && "baseUrl" in tmpConf) {
            return {
                id: tmpConf.id,
                baseUrl: tmpConf.baseUrl,
            };
        }
        return null;
    };

    const salesforceConfig = (): SalesForce.Config | null => {
        const tmpConf = get(config);
        if (
            shopType.value === ShopCore.Type.SWAROVSKI &&
            tmpConf &&
            "graphUrl" in tmpConf &&
            "shopServiceUrl" in tmpConf &&
            "shopUrl" in tmpConf
        ) {
            return {
                graphUrl: tmpConf.graphUrl,
                id: tmpConf.id,
                shopUrl: tmpConf.shopUrl,
                shopServiceUrl: tmpConf.shopServiceUrl, // points to backend microservice
            };
        }
        return null;
    };

    const sapConfig = (): Sap.Config | null => {
        const tmpConf = get(config);
        if (
            shopType.value === ShopCore.Type.BOBST &&
            tmpConf &&
            "graphUrl" in tmpConf &&
            "shopServiceUrl" in tmpConf &&
            "shopUrl" in tmpConf &&
            "cartIdParameter" in tmpConf &&
            "appEntryParameter" in tmpConf
        ) {
            return {
                graphUrl: tmpConf.graphUrl,
                id: tmpConf.id,
                shopUrl: tmpConf.shopUrl,
                shopServiceUrl: tmpConf.shopServiceUrl, // points to backend microservice
                cartIdParameter: tmpConf.cartIdParameter,
                appEntryParameter: tmpConf.appEntryParameter,
            };
        }
        return null;
    };

    const getShopLightConfig = (): ShopLight.Config | null => {
        const tmpConf = get(config);
        if (
            shopType.value === ShopCore.Type.SHOP_LIGHT &&
            tmpConf &&
            "shopEMail" in tmpConf &&
            "shopServiceUrl" in tmpConf
        ) {
            return {
                id: tmpConf.id,
                shopEMail: tmpConf.shopEMail,
                shopServiceUrl: tmpConf.shopServiceUrl,
            };
        }
        return null;
    };

    /**
     * Resolves and stores the generic shop session (QIT-6924).
     *
     * Runs alongside the provider chain below without taking part in it: the
     * generic path has no adapter yet, so picking `ShopCore.Type.GENERIC` as
     * the active provider is deliberately left to QIT-7074. Without the
     * `dev_generic_shop` flag this is a no-op and nothing is stored.
     *
     * @returns the session, or null when the flag is off or the query failed
     */
    const initGenericShopSession = async (): Promise<GenericShop.Session | null> => {
        const hasGenericShopFeatureAccess = await useHasAccessAsync({
            featureID: AccessFeature.dev_generic_shop,
            resource: AccessResource.shop,
            action: AccessAction.query,
        });
        set(isGenericShopEnabled, hasGenericShopFeatureAccess);

        if (!hasGenericShopFeatureAccess) {
            set(genericShopSession, null);
            return null;
        }

        const [session] = await Promise.all([loadGenericShopSession(), refreshGenericShopDemo()]);
        set(genericShopSession, session);
        return session;
    };

    /** Reads the shop configuration again, e.g. when the user returns to the tab. */
    const refreshGenericShopDemo = async () => {
        set(isGenericShopDemo, await loadIsGenericShopDemo());
    };

    /**
     * Asks the shop again whether the user is logged in, and stores the answer.
     *
     * Call this after a shop call failed on an open page. The usual reason is
     * a restart of the shop middleware, which forgets all logins (QIT-7382).
     * `isShopOnline` stays as it is, so the widget does not change under the
     * user. To start the login, call {@link startGenericShopLogin}.
     */
    const refreshGenericShopSession = async (): Promise<GenericShop.Session> => {
        const session = await loadGenericShopSession();
        set(genericShopSession, session);
        return session;
    };

    /**
     * Sends the browser to the generic shop login if the stored session says
     * the user must log in.
     *
     * - At most once per page, so several failed calls cannot start several
     *   logins. A later call still returns true, because the browser is still
     *   on its way to the login page.
     * - Saves the current page first, so the landing page returns the user to
     *   it after the login. The login on page load in `shop.ability.ts` does
     *   the same.
     * - Same rules as the landing page (QIT-7263): starts only when the login
     *   is missing or expired, never for `AUTH_ERROR`, and sets the "login
     *   attempted" marker, so a failed login cannot loop.
     *
     * @returns true when the browser is on its way to the login page
     */
    const startGenericShopLogin = (): boolean => {
        // Already on the way: a second failed click must not show its error.
        if (get(genericShopLoginStarted)) {
            return true;
        }

        const session = get(genericShopSession);
        if (!session || !canStartLogin(session) || !session.loginPath) {
            return false;
        }

        set(genericShopLoginStarted, true);
        set(appEntryPath, window.location.pathname + window.location.search);
        redirectToGenericLogin(session.loginPath);
        return true;
    };

    const initShopType = async (): Promise<boolean> => {
        /*
         * The generic shop replaces the per-provider adapters wherever the tenant
         * has it (QIT-6603), so it is chosen ahead of the chain below rather than
         * taking part in it — the `dev_generic_shop` flag alone decides, which
         * keeps the active provider deterministic.
         *
         * There is deliberately no fallback to a legacy adapter when the session
         * is unusable: falling back would make the provider depend on shop
         * availability, which is exactly the kind of client-side shop rule
         * QIT-7073 removes. An unusable session is reported as an offline shop
         * instead, and QIT-7076 turns the authentication cases into a login
         * redirect (QIT-7074 OQ-3).
         *
         * Unlike the legacy providers there is no config to fetch: the shop
         * service holds the provider configuration, and the frontend holds no
         * URL, token or basket id.
         */
        const genericShopSession = await initGenericShopSession();
        if (genericShopSession) {
            set(shopType, ShopCore.Type.GENERIC);
            set(config, null);
            set(isShopOnline, isSessionReady(genericShopSession));
            return true;
        }

        let hasValidConfig = false;
        const hasFdiFeatureAccess = await useHasAccessAsync({
            featureID: AccessFeature.fdi_shop_connector,
            resource: AccessResource.shop,
            action: AccessAction.query,
        });
        if (hasFdiFeatureAccess) {
            const tmpConfig = await loadFdiShopConfig();
            if (tmpConfig !== null) {
                set(shopType, ShopCore.Type.FDI);
                set(config, tmpConfig);
                hasValidConfig = true;
            }
        }

        const hasSwarovskiFeatureAccess = await useHasAccessAsync({
            featureID: AccessFeature.swarovski_shop_connector,
            resource: AccessResource.shop,
            action: AccessAction.query,
        });

        if (hasSwarovskiFeatureAccess && !hasValidConfig) {
            const tmpConfig = await loadSalesforceShopConfig();
            if (tmpConfig !== null) {
                set(shopType, ShopCore.Type.SWAROVSKI);
                set(config, tmpConfig);
                hasValidConfig = true;
            }
        }

        const hasBobstFeatureAccess = await useHasAccessAsync({
            featureID: AccessFeature.bobst_shop_connector,
            resource: AccessResource.shop,
            action: AccessAction.query,
        });

        if (hasBobstFeatureAccess && !hasValidConfig) {
            const tmpConfig = await loadSapShopConfig();
            if (tmpConfig !== null) {
                set(shopType, ShopCore.Type.BOBST);
                set(config, tmpConfig);
                hasValidConfig = true;
            }
        }

        const hasShopLightFeatureAccess = await useHasAccessAsync({
            featureID: AccessFeature.shop_light,
            resource: AccessResource.shop,
            action: AccessAction.query,
        });

        if (hasShopLightFeatureAccess && !hasValidConfig) {
            const tmpConfig = await loadShopLightConfig();
            if (tmpConfig !== null) {
                set(shopType, ShopCore.Type.SHOP_LIGHT);
                set(config, tmpConfig);
                hasValidConfig = true;
            }
        }

        return hasValidConfig;
    };

    const isShopLight = computed(() => {
        return shopType.value === ShopCore.Type.SHOP_LIGHT;
    });

    const setAppEntryPath = (path: string) => {
        set(appEntryPath, path);
    };

    /** Marks the login attempt, then sends the browser to the generic shop login. */
    const redirectToGenericLogin = (loginPath: string) => {
        set(genericLoginAttempted, true);
        window.location.href = loginPath;
    };

    const clearGenericLoginAttempt = () => {
        set(genericLoginAttempted, false);
    };

    /**
     * Set the shop type (FDI or SW) in the store
     * @param type - The type of the shop (FDI or SW)
     */
    const setShopType = (type: ShopCore.Type) => {
        set(shopType, type);
    };

    const setConfig = (newConfig: NonNullable<typeof config.value>) => {
        set(config, newConfig);
    };

    return {
        appEntryPath: computed(() => get(appEntryPath)),
        genericLoginAttempted: computed(() => get(genericLoginAttempted)),
        genericShopSession: computed(() => get(genericShopSession)),
        isGenericShopDemo: computed(() => get(isGenericShopDemo)),
        isGenericShopEnabled: computed(() => get(isGenericShopEnabled)),
        isShopOnline: computed(() => get(isShopOnline)),
        shopType: computed(() => get(shopType)),
        fdiShopConfig,
        salesforceConfig,
        sapConfig,
        getShopLightConfig,
        checkConnection,
        checkFdiConnection,
        clearGenericLoginAttempt,
        initGenericShopSession,
        initShopType,
        isShopLight,
        mockGenericShopSession,
        mockStoreOffline,
        mockStoreOnline,
        redirectToGenericLogin,
        refreshGenericShopDemo,
        refreshGenericShopSession,
        setAppEntryPath,
        setConfig,
        setShopType,
        startGenericShopLogin,
    };
});
